import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { config } from '../config';
import { SurveyPasswordGate } from './SurveyPasswordGate';
import { boardDirectorQuestions } from './boardDirectorQuestions';
import type { SurveyQuestion } from './types';
import './survey.css';

const SurveyQuestionRenderer: React.FC<{
  question: SurveyQuestion;
  value: string | string[] | number | undefined;
  onChange: (value: string | string[] | number) => void;
}> = ({ question, value, onChange }) => {
  const id = `survey-q-${question.id}`;

  switch (question.type) {
    case 'text':
    case 'email':
      return (
        <input
          id={id}
          type={question.type}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="survey-input"
        />
      );
    case 'textarea':
      return (
        <textarea
          id={id}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={question.placeholder}
          className="survey-textarea"
          rows={4}
        />
      );
    case 'radio':
      return (
        <div className="survey-options" role="radiogroup" aria-labelledby={`${id}-label`}>
          {(question.options ?? []).map((opt) => (
            <label key={opt.value} className="survey-option">
              <input
                type="radio"
                name={question.id}
                value={opt.value}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      );
    case 'checkbox': {
      const checked = Array.isArray(value) ? (value as string[]) : [];
      const toggle = (v: string) => {
        const next = checked.includes(v) ? checked.filter((x) => x !== v) : [...checked, v];
        onChange(next);
      };
      return (
        <div className="survey-options" role="group" aria-labelledby={`${id}-label`}>
          {(question.options ?? []).map((opt) => (
            <label key={opt.value} className="survey-option">
              <input
                type="checkbox"
                value={opt.value}
                checked={checked.includes(opt.value)}
                onChange={() => toggle(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      );
    }
    case 'scale':
      const min = question.scaleMin ?? 1;
      const max = question.scaleMax ?? 5;
      const step = question.scaleStep ?? 1;
      const numVal = value === undefined ? '' : Number(value);
      return (
        <div className="survey-scale">
          <div className="survey-scale-labels">
            <span>{min}</span>
            <span>{max}</span>
          </div>
          <input
            id={id}
            type="range"
            min={min}
            max={max}
            step={step}
            value={numVal === '' ? min : numVal}
            onChange={(e) => onChange(Number(e.target.value))}
            className="survey-range"
          />
          <p className="survey-scale-value">Selected: {numVal === '' ? '—' : numVal}</p>
        </div>
      );
    default:
      return (
        <input
          id={id}
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className="survey-input"
        />
      );
  }
};

const BoardDirectorSurveyContent: React.FC = () => {
  const [responses, setResponses] = useState<Record<string, string | string[] | number>>({});
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const setAnswer = useCallback((questionId: string, value: string | string[] | number) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError(null);

      const hasRequired = boardDirectorQuestions.every((q) => {
        if (!q.required) return true;
        const v = responses[q.id];
        if (Array.isArray(v)) return v.length > 0;
        return v !== undefined && String(v).trim() !== '';
      });
      if (!hasRequired) {
        setSubmitError('Please answer all required questions before submitting.');
        return;
      }

      setSubmitting(true);
      try {
        const payload = {
          survey_slug: 'board-director',
          responses: Object.entries(responses).map(([question_id, value]) => ({
            question_id,
            value,
          })),
        };
        await axios.post(`${config.BACKEND_URL}/api/surveys/submit`, payload);
        setSubmitted(true);
      } catch (err: any) {
        console.error('Survey submission error:', err);
        setSubmitError(
          err?.response?.data?.detail ||
          'There was a problem submitting your survey. Please try again.'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [responses]
  );

  if (submitted) {
    return (
      <div className="survey-page">
        <div className="survey-card survey-thanks">
          <h1>Thank you</h1>
          <p>Your responses have been recorded. We appreciate your feedback as a board director and parent.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-page">
      <div className="survey-card">
        <header className="survey-header">
          <div className="survey-org-label">OARA Youth Innovation Lab</div>
          <h1>Board Director Assessment Survey</h1>
          <div className="survey-intro-box">
            <p>
              As we look ahead to the next season, OARA is undergoing a thoughtful re-organization and re-prioritization. This survey is part of that process — to reflect on where we've been, understand our board's capacity, and make informed decisions about the future.
            </p>
            <p><strong>A few things on the horizon for next season:</strong></p>
            <ul>
              <li>Significant possibility of participating in <strong>FTC (FIRST Tech Challenge)</strong></li>
              <li>Continued participation in <strong>FLL (FIRST LEGO League)</strong></li>
              <li>Potential transition away from a fixed makerspace to <strong>on-demand rented spaces</strong> to reduce costs</li>
              <li>Increased likelihood of securing <strong>grants</strong> to build an operational reserve</li>
            </ul>
            <p>Your honest input as a board director and FLL parent is invaluable to this process. This survey is confidential.</p>
          </div>
        </header>
        <form onSubmit={handleSubmit} className="survey-form">
          {boardDirectorQuestions.map((q) => (
            <div key={q.id} className="survey-question">
              <label id={`survey-q-${q.id}-label`} className="survey-question-label">
                {q.label}
                {q.required && <span className="survey-required"> *</span>}
              </label>
              <SurveyQuestionRenderer
                question={q}
                value={responses[q.id]}
                onChange={(v) => setAnswer(q.id, v)}
              />
            </div>
          ))}
          {submitError && (
            <div className="survey-submit-error">{submitError}</div>
          )}
          <div className="survey-actions">
            <button type="submit" className="survey-submit-btn" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit survey'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const BoardDirectorSurvey: React.FC = () => {
  return (
    <SurveyPasswordGate surveySlug="boardDirector" title="Board Director Survey">
      <BoardDirectorSurveyContent />
    </SurveyPasswordGate>
  );
};

export default BoardDirectorSurvey;
